import React, { useEffect, useState } from "react";
import { Button, Select, TextInput } from "@mantine/core";
import flat from "flat";
import { BeatLoader } from "react-spinners";
import { DatePicker } from "@mantine/dates";
import {
  differenceInMonths,
  differenceInYears,
  differenceInDays,
  addYears,
  addMonths,
  addDays,
  parseISO,
  subDays,
  formatISO,
} from "date-fns";
import {
  formatToIndianNumbering,
  loadingStates,
} from "../../utilities/utilities";
import { showNotification } from "@mantine/notifications";
import { apiWrapWithErrorWithData } from "../../utilities/apiHelpers";
import { getBuList } from "../../utilities/apis/users";

export const INDIA_STATES_UNIONS = [
  { label: "Andhra Pradesh", value: "Andhra Pradesh" },
  { label: "Karnataka", value: "Karnataka" },
  {
    label: "Andaman and Nicobar Islands",
    value: "Andaman and Nicobar Islands",
  },
  { label: "Arunachal Pradesh", value: "Arunachal Pradesh" },
  { label: "Assam", value: "Assam" },
  { label: "Bihar", value: "Bihar" },
  { label: "Chandigarh", value: "Chandigarh" },
  { label: "Chattisgarh", value: "Chattisgarh" },
  { label: "Dadra and Nagar Haveli", value: "Dadra and Nagar Haveli" },
  { label: "Daman and Diu", value: "Daman and Diu" },
  { label: "Delhi", value: "Delhi" },
  { label: "Goa", value: "Goa" },
  { label: "Gujarat", value: "Gujarat" },
  { label: "Haryana", value: "Haryana" },
  { label: "Himachal Pradesh", value: "Himachal Pradesh" },
  { label: "Jammu and Kashmir", value: "Jammu and Kashmir" },
  { label: "Jharkhand", value: "Jharkhand" },
  { label: "Kerala", value: "Kerala" },
  { label: "Lakshadweep Islands", value: "Lakshadweep Islands" },
  { label: "Madhya Pradesh", value: "Madhya Pradesh" },
  { label: "Maharashtra", value: "Maharashtra" },
  { label: "Manipur", value: "Manipur" },
  { label: "Meghalaya", value: "Meghalaya" },
  { label: "Mizoram", value: "Mizoram" },
  { label: "Nagaland", value: "Nagaland" },
  { label: "Odisha", value: "Odisha" },
  { label: "Pondicherry", value: "Pondicherry" },
  { label: "Punjab", value: "Punjab" },
  { label: "Rajasthan", value: "Rajasthan" },
  { label: "Sikkim", value: "Sikkim" },
  { label: "Tamil Nadu", value: "Tamil Nadu" },
  { label: "Telangana", value: "Telangana" },
  { label: "Tripura", value: "Tripura" },
  { label: "Uttar Pradesh", value: "Uttar Pradesh" },
  { label: "Uttarakhand", value: "Uttarakhand" },
  { label: "West Bengal", value: "West Bengal" },
];

const AgreementForm2 = ({ form, setUiConfigs, uiConfigs, save, edit }) => {
  const agreementFormJson = flat.unflatten(form.values);

  const [businessUnitsOptions, setBusinessUnitsOptions] = useState([]);

  useEffect(() => {
    const fetchBusinessUnits = async () => {
      try {
        const response = await apiWrapWithErrorWithData(getBuList());

        if (response.success) {
          const formattedData = response.result.map(({ id, name }) => ({
            value: id.toString(),
            label: name,
          }));
          setBusinessUnitsOptions(formattedData);
        }
      } catch (error) {
        console.error("Error fetching business units:", error);
      }
    };
    fetchBusinessUnits();
  }, []);

  const reminderLabels = {
    15: "15 days",
    30: "30 days",
    45: "45 days",
    60: "60 days",
  };

  const validate = () => {
    const keys = {};
    if (
      !agreementFormJson.dateOfContract ||
      isNaN(parseISO(agreementFormJson.dateOfContract).getTime())
    ) {
      keys["dateOfContract"] = "please enter Starting date.";
    }
    if (
      !agreementFormJson.dueDateOfContract ||
      isNaN(parseISO(agreementFormJson.dueDateOfContract).getTime())
    ) {
      keys["dueDateOfContract"] = "please enter Ending date.";
    }

    if (!agreementFormJson.businessUnit) {
      keys["businessUnit"] = "please enter business Unit.";
    }

    if (
      agreementFormJson.terminationNoticePeriod == null ||
      agreementFormJson.terminationNoticePeriod === ""
    ) {
      keys["terminationNoticePeriod"] =
        "please enter termination notice period.";
    }

    if (!hasDuration && !hasEndDate) {
      keys.noOfYears = "duration or end date is required.";
      keys.dueDateOfContract = "duration or end date is required.";
    }

    if (
      !hasEndDate &&
      Number(agreementFormJson.noOfYears || 0) <= 0 &&
      Number(agreementFormJson.noOfMonths || 0) <= 0 &&
      Number(agreementFormJson.noOfDays || 0) <= 0
    ) {
      keys.noOfYears = "please check value.";
      keys.noOfMonths = "please check value.";
      keys.noOfDays = "please check value.";
    }

    if (Object.keys(keys).length !== 0) {
      form.setErrors(keys);
      showNotification({
        color: "red",
        title: "agreement form",
        message: "please check all fields are filled properly.",
      });
    }
    return Object.keys(keys).length === 0;
  };

  const savePrev = async () => {
    if (true) {
      setUiConfigs({
        ...uiConfigs,
        currentStep: 0,
      });
    }
  };

  const saveNext = async () => {
    if (validate()) {
      await save({ ...form.values });
      setUiConfigs({
        ...uiConfigs,
        currentStep: 2,
      });
    }
  };

  const handleChange = (e) => {
    const inputValue = e.target.value.replace(/,/g, ""); // Remove commas
    const numericValue = parseInt(inputValue, 10); // Convert to integer

    if (!isNaN(numericValue)) {
      form.setFieldValue("agreementValue", numericValue);
    } else {
      form.setFieldValue("agreementValue", 0);
    }
  };

  const handleTerminationNoticePeriodChange = (e) => {
    const inputValue = e.target.value.replace(/\D/g, "");
    if (inputValue === "") {
      form.setFieldValue("terminationNoticePeriod", null);
      return;
    }
    form.setFieldValue("terminationNoticePeriod", parseInt(inputValue, 10));
  };

  const hasDuration =
    Number(form.values.noOfYears || 0) > 0 ||
    Number(form.values.noOfMonths || 0) > 0 ||
    Number(form.values.noOfDays || 0) > 0;

  const hasEndDate = Boolean(form.values.dueDateOfContract);

  const resetDuration = () => {
    form.setFieldValue("noOfYears", "0");
    form.setFieldValue("noOfMonths", "0");
    form.setFieldValue("noOfDays", "0");
    form.setFieldValue("dueDateOfContract", null);
  };

  const calculateEndDateFromDuration = (startISO, years, months, days) => {
    if (!startISO) return null;
    let date = parseISO(startISO);
    if (years) date = addYears(date, Number(years));
    if (months) date = addMonths(date, Number(months));
    if (days) date = addDays(date, Number(days));
    return formatISO(subDays(date, 1));
  };

  const calculateDurationFromDates = (startISO, endISO) => {
    const start = parseISO(startISO);
    const end = addDays(parseISO(endISO), 1);
    if (end <= start) return { years: "0", months: "0", days: "0" };
    const years = differenceInYears(end, start);
    const afterYears = addYears(start, years);
    const months = differenceInMonths(end, afterYears);
    const afterMonths = addMonths(afterYears, months);
    const days = differenceInDays(end, afterMonths);
    return {
      years: years.toString(),
      months: months.toString(),
      days: days.toString(),
    };
  };

  const handleDurationChange = (field, value) => {
    const nextValues = {
      ...form.values,
      [field]: value ?? "0",
    };

    form.setValues(nextValues);

    if (!nextValues.dateOfContract) return;

    const years = Number(nextValues.noOfYears || 0);
    const months = Number(nextValues.noOfMonths || 0);
    const days = Number(nextValues.noOfDays || 0);

    const endISO = calculateEndDateFromDuration(
      nextValues.dateOfContract,
      years,
      months,
      days,
    );

    form.setFieldValue("dueDateOfContract", endISO);
  };

  useEffect(() => {
    if (edit) {
      const endISO = calculateEndDateFromDuration(
        form?.values?.dateOfContract,
        form?.values?.noOfYears,
        form?.values?.noOfMonths,
        form?.values?.noOfDays,
      );
      form.setFieldValue("dueDateOfContract", endISO);
    }
  }, [edit]);

  return (
    <div className="w-full flex flex-col my-4">
      <div className="max-w-lg pl-10 flex flex-col gap-4">
        <DatePicker
          required
          label="Starting date"
          placeholder="select starting date"
          {...{
            ...form.getInputProps("dateOfContract"),
            onChange: (val) => {
              if (val && val.getTime) {
                form.getInputProps("dateOfContract").onChange(formatISO(val));
              } else {
                form.getInputProps("dateOfContract").onChange(null);
              }
            },
            value: form.values["dateOfContract"]
              ? parseISO(form.values.dateOfContract)
              : null,
          }}
        />
        <div className="flex flex-row">
          <Select
            label="Duration"
            required
            placeholder="years"
            data={[
              ...Array.from({ length: 11 }, (_, i) => ({
                value: `${i}`,
                label: `${i} years`,
              })),
              { value: "100", label: "100 years" },
            ]}
            value={form.values.noOfYears}
            onChange={(val) => handleDurationChange("noOfYears", val)}
            style={{ width: "160px", marginRight: "20px" }}
          />
          <Select
            label=" "
            placeholder="months"
            data={[
              ...Array.from({ length: 13 }, (_, i) => ({
                value: `${i}`,
                label: `${i} months`,
              })),
            ]}
            value={form.values.noOfMonths}
            onChange={(val) => handleDurationChange("noOfMonths", val)}
            style={{ width: "160px", marginRight: "20px" }}
          />
          <Select
            label=" "
            placeholder="days"
            data={[
              ...Array.from({ length: 32 }, (_, i) => ({
                value: `${i}`,
                label: `${i} days`,
              })),
            ]}
            value={form.values.noOfDays}
            onChange={(val) => handleDurationChange("noOfDays", val)}
            style={{ width: "160px" }}
          />
        </div>

        <DatePicker
          label="End date"
          required
          placeholder="select end date"
          onChange={(val) => {
            if (!val || !form.values.dateOfContract) {
              resetDuration();
              return;
            }

            const endISO = formatISO(val);

            const { years, months, days } = calculateDurationFromDates(
              form.values.dateOfContract,
              endISO,
            );

            form.setValues((prev) => ({
              ...prev,
              dueDateOfContract: endISO,
              noOfYears: String(years),
              noOfMonths: String(months),
              noOfDays: String(days),
            }));
          }}
          value={
            form.values.dueDateOfContract
              ? parseISO(form.values.dueDateOfContract)
              : null
          }
        />

        <TextInput
          value={formatToIndianNumbering(agreementFormJson.agreementValue)}
          label="Annual agreement value (₹)"
          placeholder="annual agreement value"
          onChange={handleChange}
        />
        <Select
          placeholder="reminder time"
          label="Renewal reminder time (in days)"
          data={[
            { value: 15, label: reminderLabels["15"] },
            { value: 30, label: reminderLabels["30"] },
            { value: 45, label: reminderLabels["45"] },
            { value: 60, label: reminderLabels["60"] },
          ]}
          {...form.getInputProps("reminderTimeInDays")}
        />
        <TextInput
          required
          value={
            form.values.terminationNoticePeriod != null
              ? String(form.values.terminationNoticePeriod)
              : ""
          }
          label="Termination notice period (in days)"
          placeholder="enter number of days"
          error={form.errors.terminationNoticePeriod}
          onChange={handleTerminationNoticePeriodChange}
        />
        <Select
          required
          searchable
          placeholder="select business unit"
          label="Business unit"
          data={businessUnitsOptions}
          {...form.getInputProps("businessUnit")}
          dropdownPosition="bottom"
        />
      </div>
      <div className="flex items-center justify-between mt-20">
        <Button
          className="max-w-xs "
          onClick={savePrev}
          style={{
            backgroundColor: "#46BDE1",
          }}
          disabled={uiConfigs.loading === loadingStates.LOADING}
        >
          Previous
        </Button>
        <Button
          className="max-w-xs"
          style={{
            backgroundColor: "#46BDE1",
          }}
          onClick={saveNext}
          disabled={uiConfigs.loading === loadingStates.LOADING}
        >
          {uiConfigs.loading === loadingStates.LOADING ? (
            <BeatLoader color={"#fff"} size={10} />
          ) : (
            "Save & Next"
          )}
        </Button>
      </div>
    </div>
  );
};

export default AgreementForm2;
